import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { faEdit, faEye, faCheck, faCopy, faShieldKey, faKey, faTimesCircle, faQrcode, faClockRotateLeft, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Application } from '../../core/app';
import { escapeHtml } from '../../utils/sanitize';
import { enableVaultItemTotp, getVaultItemTotpCode, disableVaultItemTotp, getVaultItemHistory, restoreVaultItemVersion } from '../../api/backend';
import '../Vault/Vault.css';

export default function VaultDetail(props) {

  let { vaultItemName, 
        vaultItemId,
        vaultItemDetails,
        vaultItemValue,
        vaultItemVaultId,
        vaultItemTotpEnabled,
        vaults,
        selectedVault,
        revealVaultItem,
        updateVaultItem,
        hideTrigger = false,
        open: controlledOpen,
        onOpenChange
       } = props;

  const isControlled = hideTrigger === true;
  const [localOpen, setLocalOpen] = React.useState(false);
  const open = isControlled ? !!controlledOpen : localOpen;
  const setOpen = isControlled
    ? (val) => { if (onOpenChange) onOpenChange(val, vaultItemId); }
    : setLocalOpen;
  const [edit, setEdit] = React.useState(false);
  const [selectedVaultId, setSelectedVaultId] = React.useState(vaultItemVaultId || '');
  // Local edit state — independent per instance, synced from props when opening edit
  const [editName, setEditName] = React.useState(vaultItemName);
  const [editDetails, setEditDetails] = React.useState(vaultItemDetails);
  const [editValue, setEditValue] = React.useState(vaultItemValue);

  // 2FA state
  const [totpEnabled, setTotpEnabled] = React.useState(false);
  const [totpSetupOpen, setTotpSetupOpen] = React.useState(false);
  const [totpInputSecret, setTotpInputSecret] = React.useState('');
  const [totpSaving, setTotpSaving] = React.useState(false);
  const [totpSaveError, setTotpSaveError] = React.useState('');
  const [totpDisabling, setTotpDisabling] = React.useState(false);
  const [totpCode, setTotpCode] = React.useState('');
  const [totpLoadingCode, setTotpLoadingCode] = React.useState(false);
  const [totpCountdown, setTotpCountdown] = React.useState(30);

  // Version history state
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyVersions, setHistoryVersions] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [restoringVersion, setRestoringVersion] = React.useState(null);

  // Auto-refresh TOTP code every 30 seconds — only when the modal is open AND this item has 2FA
  React.useEffect(() => {
    // Only fetch when this specific modal is open and this item has 2FA enabled
    if (!open || !totpEnabled) {
      if (!open) setTotpCountdown(30);
      return;
    }

    const fetchCode = () => {
      getVaultItemTotpCode(vaultItemId).then(res => {
        setTotpCode(res.data.code);
      }).catch(() => {});
    };

    // Fetch immediately on open
    fetchCode();

    const interval = setInterval(() => {
      setTotpCountdown(prev => {
        if (prev <= 1) {
          fetchCode();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, totpEnabled, vaultItemId]);

  // Sync local state when the viewed item changes (props change)
  React.useEffect(() => {
    setEditName(vaultItemName);
    setEditDetails(vaultItemDetails);
    setEditValue(vaultItemValue);
    setSelectedVaultId(vaultItemVaultId || '');
    setTotpEnabled(!!vaultItemTotpEnabled);
    setTotpCode('');
    setTotpCountdown(30);
  }, [vaultItemName, vaultItemDetails, vaultItemValue, vaultItemVaultId, vaultItemTotpEnabled]);

  const handleOpen = () => {
    setSelectedVaultId(vaultItemVaultId || '');
    setOpen(true);
    setEdit(false);
    setTotpCountdown(30);
    revealVaultItems();
  }
  const handleClose = () => {
    setOpen(false);
    setEdit(false);
    setSelectedVaultId('');
  }

  const handleEditOpen = () => {
    // Sync local edit state from current props before entering edit mode
    setEditName(vaultItemName);
    setEditDetails(vaultItemDetails);
    setEditValue(vaultItemValue);
    setSelectedVaultId(vaultItemVaultId || '');
    setEdit(true);
  }
  const handleEditClose = () => setEdit(false);

  const revealVaultItems = () => {
    revealVaultItem(vaultItemId);
  }

  const handleSubmit = (event) => {
    event.preventDefault();

    const customEvent = {
      target: {
        vaultItemName: { value: editName },
        vaultItemDetails: { value: editDetails },
        vaultItemValue: { value: editValue },
        vaultItemVaultId: { value: selectedVaultId || null }
      }
    };

    updateVaultItem(vaultItemId, customEvent);
    handleEditClose();
  }

  // ---- History handlers ----

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryVersions([]);
    getVaultItemHistory(vaultItemId).then(res => {
      setHistoryVersions(res.data || []);
      setHistoryLoading(false);
    }).catch(() => {
      setHistoryLoading(false);
    });
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setHistoryVersions([]);
  };

  const handleRestoreVersion = (versionId) => {
    if (!window.confirm('Restore this item to this previous version? The current version will be saved as a new history entry.')) {
      return;
    }
    setRestoringVersion(versionId);
    restoreVaultItemVersion(vaultItemId, versionId).then(res => {
      setRestoringVersion(null);
      // Refresh the current item data
      revealVaultItems();
      handleCloseHistory();
    }).catch(() => {
      setRestoringVersion(null);
    });
  };

  // ---- 2FA handlers ----

  const handleOpenTotpSetup = () => {
    setTotpSetupOpen(true);
    setTotpInputSecret('');
    setTotpSaveError('');
  };

  const handleCloseTotpSetup = () => {
    setTotpSetupOpen(false);
    setTotpInputSecret('');
    setTotpSaveError('');
  };

  const handleSaveTotpSecret = () => {
    setTotpSaving(true);
    setTotpSaveError('');
    enableVaultItemTotp(vaultItemId, totpInputSecret).then(res => {
      setTotpSaving(false);
      setTotpEnabled(true);
      setTotpCode(res.data.code);
      setTotpCountdown(30);
      handleCloseTotpSetup();
    }).catch(err => {
      setTotpSaving(false);
      setTotpSaveError(err.response?.data?.message || 'Failed to save secret.');
    });
  };

  const handleRefreshCode = () => {
    setTotpLoadingCode(true);
    setTotpCountdown(30);
    getVaultItemTotpCode(vaultItemId).then(res => {
      setTotpCode(res.data.code);
      setTotpLoadingCode(false);
    }).catch(() => {
      setTotpLoadingCode(false);
    });
  };

  const handleDisableTotp = () => {
    setTotpDisabling(true);
    disableVaultItemTotp(vaultItemId).then(res => {
      setTotpDisabling(false);
      setTotpEnabled(false);
      setTotpCode('');
    }).catch(() => {
      setTotpDisabling(false);
    });
  };

  const vaultDetails = () => {

    if (edit) {
      const nameId = `vault-edit-name-${vaultItemId}`;
      const vaultId = `vault-edit-vault-${vaultItemId}`;
      const detailsId = `vault-edit-details-${vaultItemId}`;
      const valueId = `vault-edit-value-${vaultItemId}`;
      return (
        <div>
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              <div className='field-stack'>
                <label className="field-label" htmlFor={nameId}>Name</label>
                <div className='add-item-box add-item-box--field'>
                  <input
                    id={nameId}
                    value={editName}
                    name="vaultItemName"
                    className='add-item-input'
                    onChange={(e) => setEditName(e.target.value)}
                  />
                </div>
              </div>
            </DialogTitle>
            <DialogContent>
              <div className='field-stack'>
                <label className="field-label" htmlFor={vaultId}>Vault</label>
                <div className='add-item-box add-item-box--field'>
                  <select
                    id={vaultId}
                    value={selectedVaultId}
                    onChange={(e) => setSelectedVaultId(e.target.value)}
                    className='add-item-input'
                  >
                    <option value="">Select Vault</option>
                    {vaults && vaults.length > 0 && vaults.map((vault) => (
                      <option key={vault.id} value={vault.id}>
                        {escapeHtml(vault.name)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='field-stack'>
                <label className="field-label" htmlFor={detailsId}>Details</label>
                <div className='add-item-box add-item-box--field'>
                  <input
                    id={detailsId}
                    value={editDetails}
                    name="vaultItemDetails"
                    className='add-item-input'
                    onChange={(e) => setEditDetails(e.target.value)}
                    placeholder="Details"
                  />
                </div>
              </div>
              <div className='field-stack'>
                <label className="field-label" htmlFor={valueId}>Value</label>
                <div className='add-item-box add-item-box--field'>
                  <input
                      id={valueId}
                      value={editValue}
                      name="vaultItemValue"
                      className='add-item-input'
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder="Value"
                    />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button type="submit" variant="contained" color="primary" size="large">
                Save
              </Button>
            </DialogActions>
          </form>
        </div>
      );
    }

    return (
      <div className='vault-detail-dialog'>
        <DialogTitle>{escapeHtml(vaultItemName)}</DialogTitle>
        <DialogContent>
          <div className='vault-detail-fields'>
            <div className='vault-detail-field'>
              <span className='vault-detail-field-label'>Details</span>
              <span className='vault-detail-field-value'>{escapeHtml(vaultItemDetails)}</span>
              <button className='vault-detail-copy-btn' onClick={() => navigator.clipboard.writeText(vaultItemDetails)} title="Copy">
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
            <div className='vault-detail-field'>
              <span className='vault-detail-field-label'>Value</span>
              <span className='vault-detail-field-value'>{escapeHtml(vaultItemValue)}</span>
              <button className='vault-detail-copy-btn' onClick={() => navigator.clipboard.writeText(vaultItemValue)} title="Copy">
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
          </div>
          {/* 2FA section */}
          <div className='vault-totp-section'>
            <div className='vault-totp-header'>
              <FontAwesomeIcon icon={faShieldKey} style={{ color: totpEnabled ? '#4caf50' : '#888' }} />
              <span>{totpEnabled ? '2FA Code' : '2FA Disabled'}</span>
            </div>
            {totpEnabled ? (
              <div>
                <div className='vault-totp-code'>
                  <span className='vault-totp-code-value'>
                    {totpLoadingCode ? '...' : totpCode || '...'}
                  </span>
                  {totpCode && (
                    <button className='vault-detail-copy-btn' onClick={() => navigator.clipboard.writeText(totpCode)} title="Copy code">
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  )}
                </div>
                <div className='vault-totp-timer'>
                  <svg width="14" height="14" viewBox="0 0 18 18">
                    <circle cx="9" cy="9" r="7" fill="none" stroke="#888" strokeWidth="2" strokeDasharray={`${(totpCountdown / 30) * 44} 44`} strokeLinecap="round" transform="rotate(-90 9 9)" />
                  </svg>
                  <span>Refreshing in {totpCountdown}s</span>
                </div>
                <div className='vault-totp-actions'>
                  <button
                    className='vault-totp-action-btn vault-totp-action-btn--refresh'
                    onClick={handleRefreshCode}
                    disabled={totpLoadingCode}
                  >
                    <FontAwesomeIcon icon={faQrcode} /> Refresh
                  </button>
                  <button
                    className='vault-totp-action-btn vault-totp-action-btn--disable'
                    onClick={handleDisableTotp}
                    disabled={totpDisabling}
                  >
                    <FontAwesomeIcon icon={faTimesCircle} /> {totpDisabling ? 'Disabling...' : 'Disable'}
                  </button>
                </div>
              </div>
            ) : (
              <div className='vault-totp-actions'>
                <button
                  className='vault-totp-action-btn vault-totp-action-btn--enable'
                  onClick={handleOpenTotpSetup}
                >
                  <FontAwesomeIcon icon={faKey} /> Enable 2FA
                </button>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <button className='vault-totp-action-btn vault-totp-action-btn--refresh' onClick={handleEditOpen}>
            <FontAwesomeIcon icon={faEdit} /> Edit
          </button>
          <button className='vault-totp-action-btn vault-totp-action-btn--refresh' onClick={handleOpenHistory}>
            <FontAwesomeIcon icon={faClockRotateLeft} /> History
          </button>
        </DialogActions>
      </div>
    );
  }

  // Show vault name tag when filtering by "All"
  const showVaultTag = selectedVault === '0' && vaultItemVaultId;
  const vaultName = showVaultTag
    ? (vaults?.find(v => v.id === vaultItemVaultId)?.name || '')
    : '';

  return (
    <div>
        {!hideTrigger && (
          <div className='vault-item-trigger' onClick={handleOpen}>
            <div className='vault-item-name-row'>
                <FontAwesomeIcon icon={faEye} />
                <span>{escapeHtml(vaultItemName)}</span>
                {totpEnabled && (
                  <span style={{ marginLeft: '8px', color: '#4caf50' }}>
                    <FontAwesomeIcon icon={faShieldKey} title="2FA Enabled" />
                  </span>
                )}
            </div>
            {showVaultTag && vaultName && (
              <span className='vault-tag'>{escapeHtml(vaultName)}</span>
            )}
          </div>
        )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="vault-detail-title"
        aria-describedby="vault-detail-description"
        fullWidth
        maxWidth="md"
      >
        { vaultDetails() }
      </Dialog>
      {/* 2FA Setup Dialog - Enter TOTP Secret */}
      <Dialog
        open={totpSetupOpen}
        onClose={handleCloseTotpSetup}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faShieldKey} style={{ color: '#4caf50' }} />
            Add 2FA Secret
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: '12px' }}>
            Enter the TOTP secret from your authenticator app or service. This is typically a base32-encoded string (e.g., <code>JBSWY3DPEHPK3PXP</code>).
          </Typography>
          <div className='field-stack'>
            <label className="field-label" htmlFor="totp-secret-input">TOTP Secret</label>
            <div className='add-item-box add-item-box--field'>
              <input
                id="totp-secret-input"
                value={totpInputSecret}
                onChange={(e) => {
                  setTotpInputSecret(e.target.value.toUpperCase().replace(/[^A-Z2-7=]/g, ''));
                  setTotpSaveError('');
                }}
                className='add-item-input'
                placeholder="JBSWY3DPEHPK3PXP"
                style={{ fontFamily: 'monospace', letterSpacing: '2px', textTransform: 'uppercase' }}
              />
            </div>
          </div>
          {totpSaveError && (
            <Typography variant="body2" style={{ color: '#f44336', marginTop: '8px' }}>
              {totpSaveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <button onClick={handleCloseTotpSetup}>Cancel</button>
          <button
            onClick={handleSaveTotpSecret}
            disabled={totpSaving || totpInputSecret.length < 16}
            style={{
              backgroundColor: '#4caf50',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: totpSaving || totpInputSecret.length < 16 ? 'not-allowed' : 'pointer',
              opacity: totpSaving || totpInputSecret.length < 16 ? 0.5 : 1
            }}
          >
            {totpSaving ? 'Saving...' : 'Save'}
          </button>
        </DialogActions>
      </Dialog>
      {/* Version History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={handleCloseHistory}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: '#90caf9' }} />
            Version History
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
              Loading history...
            </div>
          ) : historyVersions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
              No version history available. Versions are saved automatically when you edit an item.
            </div>
          ) : (
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {historyVersions.map((version, index) => (
                <div
                  key={version.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        backgroundColor: 'rgba(144,202,249,0.2)',
                        color: '#90caf9',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}>
                        v{historyVersions.length - index}
                      </span>
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        {new Date(version.changed_at).toLocaleString()}
                      </span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      by {escapeHtml(version.changed_by_name)}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gap: '4px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{ color: '#888', minWidth: '60px' }}>Name:</span>
                      <span style={{ flex: 1 }}>{escapeHtml(version.name)}</span>
                    </div>
                    {version.details && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{ color: '#888', minWidth: '60px' }}>Details:</span>
                        <span style={{ flex: 1 }}>{escapeHtml(version.details)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{ color: '#888', minWidth: '60px' }}>Value:</span>
                      <span style={{ flex: 1, fontFamily: 'monospace' }}>{escapeHtml(version.value)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => handleRestoreVersion(version.id)}
                      disabled={restoringVersion !== null}
                      style={{
                        color: '#90caf9',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <FontAwesomeIcon icon={faRotateLeft} />
                      {restoringVersion === version.id ? 'Restoring...' : 'Restore'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <button onClick={handleCloseHistory}>Close</button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
